/**
 * BacktestPage.tsx
 *
 * Main page component for the covered call backtesting feature.
 * Allows users to backtest 3 strategies against SPY/SPYI benchmarks.
 * Uses SSE streaming for real-time progress updates.
 */

import React, { useState, useCallback, useRef } from 'react';
import Layout from '../common/Layout';
import BacktestForm from './BacktestForm';
import BacktestResults from './BacktestResults';
import BacktestMissionControl from './BacktestMissionControl';
import {
  BacktestRequest,
  BacktestResponse,
  ProgressStep,
  ProgressStepId,
  LiveStats,
  DEFAULT_LIVE_STATS,
  getProgressSteps
} from '../../types/backtest';
import config from '../../config';

const BacktestPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BacktestResponse | null>(null);
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const [currentMessage, setCurrentMessage] = useState<string>('Initializing...');
  const [currentSymbol, setCurrentSymbol] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);
  const [liveStats, setLiveStats] = useState<LiveStats>(DEFAULT_LIVE_STATS);
  const abortControllerRef = useRef<AbortController | null>(null);
  const logTimestampsRef = useRef<number[]>([]);

  const handleRunBacktest = useCallback(async (request: BacktestRequest) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setCurrentSymbol(request.symbol);
    setCurrentMessage('Connecting to server...');
    setLogs([]);
    setLiveStats(DEFAULT_LIVE_STATS);
    logTimestampsRef.current = [];

    // Set progress steps based on selected deltas
    setSteps(getProgressSteps(request.deltas));

    // Create abort controller for cleanup
    abortControllerRef.current = new AbortController();

    try {
      console.log('Running backtest with SSE streaming:', request);

      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${config.API_URL}/api/backtest/run/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Streaming not supported');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          // Debug: log all non-empty lines received
          if (line.trim()) {
            console.log('[SSE-DEBUG] Received line:', line.substring(0, 100));
          }
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('[SSE-DEBUG] Parsed event:', data.type, data);

              // Handle different event types
              if (data.type === 'connected') {
                console.log('[SSE-DEBUG] SSE stream connected successfully!');
                continue;
              }

              if (data.type === 'complete') {
                // Final result received
                console.log('Backtest completed:', data.result);
                setResults(data.result);
                setLoading(false);
                return;
              }

              if (data.type === 'error') {
                throw new Error(data.message);
              }

              if (data.type === 'log') {
                // Track timestamp for events/sec calculation
                const now = Date.now();
                logTimestampsRef.current.push(now);

                // Keep only last 5 seconds of timestamps
                const fiveSecondsAgo = now - 5000;
                logTimestampsRef.current = logTimestampsRef.current.filter(t => t > fiveSecondsAgo);

                // Calculate events per second (average over 5 seconds)
                const eventsPerSecond = logTimestampsRef.current.length / 5;

                setLiveStats(prev => ({ ...prev, eventsPerSecond }));
                setLogs(prevLogs => [...prevLogs, data.text]);
                continue;
              }

              if (data.type === 'status') {
                // Update live stats from status event
                setLiveStats(prev => ({
                  ...prev,
                  currentDate: data.current_date,
                  tradesCount: data.trades_count,
                  winsCount: data.wins_count,
                  winRate: data.trades_count > 0
                    ? (data.wins_count / data.trades_count) * 100
                    : 0,
                  pnlRunning: data.pnl_running
                }));
                continue;
              }

              if (data.type === 'progress') {
                // Update progress
                const stepId = data.step as ProgressStepId;
                setCurrentMessage(data.message);

                setSteps(prevSteps => {
                  return prevSteps.map(step => {
                    if (step.id === stepId) {
                      return {
                        ...step,
                        status: data.progress >= 100 ? 'complete' : 'running',
                        progress: data.progress
                      };
                    } else if (prevSteps.findIndex(s => s.id === stepId) > prevSteps.findIndex(s => s.id === step.id)) {
                      if (step.status !== 'complete') {
                        return { ...step, status: 'complete', progress: 100 };
                      }
                    }
                    return step;
                  });
                });
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Backtest cancelled');
        return;
      }
      console.error('Backtest failed:', err);
      const errorMessage = err.message || 'Failed to run backtest';
      setError(errorMessage);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, []);

  const handleReset = useCallback(() => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setResults(null);
    setError(null);
    setLoading(false);
    setSteps([]);
    setCurrentMessage('Initializing...');
    setLogs([]);
    setLiveStats(DEFAULT_LIVE_STATS);
    logTimestampsRef.current = [];
  }, []);

  return (
    <Layout>
      <div className="app-page-container bt">
        {/* Form Section */}
        {!results && !loading && (
          <BacktestForm
            onSubmit={handleRunBacktest}
            loading={loading}
          />
        )}

        {/* Error Display */}
        {error && (
          <div className="bt-err" role="alert">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Progress State - Mission Control Layout */}
        {loading && (
          <BacktestMissionControl
            steps={steps}
            currentMessage={currentMessage}
            symbol={currentSymbol}
            logs={logs}
            isRunning={loading}
            liveStats={liveStats}
          />
        )}

        {/* Results Section */}
        {results && !loading && (
          <BacktestResults results={results} onNewBacktest={handleReset} />
        )}
      </div>
    </Layout>
  );
};

export default BacktestPage;
